import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, PageTitle } from '../../../../components/common';
import { useOrdenFacturacionStore } from '../store/useOrdenFacturacionStore';
import type { OrdenFacturacion, EstadoType } from '../types';
import { estadoLabels, estadoColors } from '../types';

const OrdenFacturacionPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const {
    ordenes,
    loading,
    error,
    fetchOrdenes,
    updateEstado,
    deleteOrden,
    clearError,
  } = useOrdenFacturacionStore();

  useEffect(() => {
    fetchOrdenes();
  }, [fetchOrdenes]);

  const filteredOrdenes = ordenes.filter((orden) => {
    switch (selectedTab) {
      case 0: return orden.estado === 'PEN';
      case 1: return orden.estado === 'APR';
      case 2: return orden.estado === 'ANU';
      case 3: return true;
      default: return true;
    }
  });

  const countByEstado = (estado: EstadoType) =>
    ordenes.filter((o) => o.estado === estado).length;

  const handleAprobar = async (id: number) => {
    await updateEstado(id, 'APR');
  };

  const handleAnular = async (id: number) => {
    await updateEstado(id, 'ANU');
  };

  const handleDelete = async (id: number) => {
    await deleteOrden(id);
  };


  const columns: GridColDef[] = [
    { field: 'numero', headerName: 'N° Orden', width: 100 },
    {
      field: 'fechaProceso',
      headerName: 'Fecha',
      width: 110,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString('es-ES') : '',
    },
    {
      field: 'cliente',
      headerName: 'Cliente',
      width: 200,
      valueGetter: (_value: unknown, row: OrdenFacturacion) =>
        row.cliente?.razonSocial || '',
    },
    { field: 'tipoProceso', headerName: 'Proceso', width: 110 },
    {
      field: 'numeroMallas',
      headerName: 'Mallas',
      width: 80,
      type: 'number',
    },
    {
      field: 'totalKilos',
      headerName: 'Total Kg',
      width: 100,
      type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2),
    },
    {
      field: 'subTotal',
      headerName: 'Sub Total',
      width: 110,
      type: 'number',
      valueFormatter: (value: number) => value ? `S/. ${value.toFixed(2)}` : '',
    },
    {
      field: 'totalFacturar',
      headerName: 'Total Facturar',
      width: 120,
      type: 'number',
      valueFormatter: (value: number) => value ? `S/. ${value.toFixed(2)}` : '',
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={estadoLabels[params.value as EstadoType] || params.value}
          color={estadoColors[params.value as EstadoType] || 'default'}
          size="small"
          sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.7rem' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ver detalle">
            <IconButton
              size="small"
              onClick={() => navigate(`/administracion/orden-facturacion/nueva?parteId=${params.row.idParte}&tipoProceso=${params.row.tipoProceso}`)}
              sx={{ color: '#64748b', '&:hover': { color: '#0f2847', bgcolor: '#f0f7ff' } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.estado === 'PEN' && (
            <>
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/administracion/orden-facturacion/nueva?parteId=${params.row.idParte}&tipoProceso=${params.row.tipoProceso}`)}
                  sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Aprobar">
                <IconButton
                  size="small"
                  onClick={() => handleAprobar(params.row.id)}
                  sx={{ color: '#10b981', '&:hover': { bgcolor: '#ecfdf5' } }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Anular">
                <IconButton
                  size="small"
                  onClick={() => handleAnular(params.row.id)}
                  sx={{ color: '#f59e0b', '&:hover': { bgcolor: '#fffbeb' } }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(params.row.id)}
                  sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  if (loading && ordenes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#3b82f6' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <PageTitle
          title="Órdenes de Facturación"
          subtitle="Gestión de Órdenes de Facturación"
        />
        {/* Generar Orden se hace desde Partes de Producción */}
      </Box>

      {successMsg && (
        <Alert
          severity="success"
          onClose={() => setSuccessMsg(null)}
          sx={{ mb: 2, borderRadius: 2, fontFamily: '"DM Sans", sans-serif' }}
        >
          {successMsg}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          onClose={clearError}
          sx={{ mb: 2, borderRadius: 2, fontFamily: '"DM Sans", sans-serif' }}
        >
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(_e, newValue) => setSelectedTab(newValue)}
          sx={{
            px: 2,
            bgcolor: '#fff',
            '& .MuiTab-root': {
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'none',
              minHeight: 48,
              color: '#64748b',
              '&.Mui-selected': { color: '#0f2847' },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #3b82f6, #10b981)',
            },
          }}
        >
          <Tab label={`Pendientes (${countByEstado('PEN')})`} />
          <Tab label={`Aprobadas (${countByEstado('APR')})`} />
          <Tab label={`Anuladas (${countByEstado('ANU')})`} />
          <Tab label={`Lista (${ordenes.length})`} />
        </Tabs>
      </Paper>

      <DataGrid
        rows={filteredOrdenes}
        columns={columns}
        loading={loading}
        getRowId={(row: OrdenFacturacion) => row.id}
        pageSize={25}
      />

    </Box>
  );
};

export default OrdenFacturacionPage;
